import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { X } from "lucide-react";
import { useStore } from "../context/Store";
import { skipCity } from "../lib/city";
import { PATTERNS, t } from "../i18n";

/**
 * A single hand-drawn SVG map of Morocco with a small pulsing pin per city that
 * currently has an active bouquet. Only Latin-name lookups are hard-coded; every
 * user-facing label goes through the localized city name from the bootstrap payload.
 *
 * SEO notes:
 *  - The modal is mounted lazily and only after hydration, so crawlers see an empty
 *    <div id="city-picker-root"> (no duplicated H1s, no keyword bloat).
 *  - The visible copy inside is short and geo-focused (matches the same phrases the
 *    city landing pages target).
 */
const CITY_COORDS = {
  // Percent positions on /brand/morocco-map.png (measured against the cropped
  // outline that runs from the northern tip down through Western Sahara).
  tangier:   { x: 74, y: 5.5,  label: "Tangier" },
  tetouan:   { x: 79, y: 4.5,  label: "Tétouan" },
  kenitra:   { x: 63, y: 15,   label: "Kénitra" },
  rabat:     { x: 66, y: 17,   label: "Rabat" },
  meknes:    { x: 75, y: 17.5, label: "Meknès" },
  fes:       { x: 79, y: 16,   label: "Fes" },
  oujda:     { x: 93, y: 12,   label: "Oujda" },
  casablanca:{ x: 60, y: 22,   label: "Casablanca" },
  eljadida:  { x: 55, y: 25,   label: "El Jadida" },
  safi:      { x: 53, y: 30,   label: "Safi" },
  marrakech: { x: 63, y: 32,   label: "Marrakech" },
  essaouira: { x: 49, y: 33,   label: "Essaouira" },
  agadir:    { x: 50, y: 40,   label: "Agadir" },
};

const COPY = {
  en: { title: "Where should we deliver today?", body: "Fleurinelle bouquets are made in the recipient's own city, delivered by hand and photographed at the door. Pick where you're sending to.", skip: "Browse everywhere", pin: "Same day" },
  fr: { title: "Où livrons-nous aujourd'hui ?", body: "Chaque bouquet Fleurinelle est composé dans la ville même du destinataire, livré en main propre et photographié à la porte. Choisissez la ville de livraison.", skip: "Voir tout le catalogue", pin: "Jour même" },
  es: { title: "¿A dónde entregamos hoy?", body: "Cada ramo Fleurinelle se prepara en la ciudad del destinatario, se entrega en mano y se fotografía en la puerta. Elija la ciudad de entrega.", skip: "Ver todo el catálogo", pin: "Mismo día" },
  ar: { title: "أين نوصل اليوم؟", body: "تُصنع باقات فلورينيل في مدينة المستلم نفسها، وتُسلَّم يدوياً وتُصوَّر عند الباب. اختر مدينة التسليم.", skip: "تصفّح كل المتجر", pin: "نفس اليوم" },
  it: { title: "Dove consegniamo oggi?", body: "I bouquet Fleurinelle sono realizzati nella città stessa del destinatario, consegnati a mano e fotografati alla porta. Scelga dove inviare.", skip: "Esplora ovunque", pin: "Stesso giorno" },
  nl: { title: "Waar bezorgen we vandaag?", body: "Fleurinelle-boeketten worden gemaakt in de eigen stad van de ontvanger, met de hand bezorgd en aan de deur gefotografeerd. Kies waar u naartoe stuurt.", skip: "Bekijk alles", pin: "Dezelfde dag" },
  de: { title: "Wohin sollen wir heute liefern?", body: "Fleurinelle-Sträuße werden in der Stadt des Empfängers selbst gefertigt, von Hand geliefert und an der Tür fotografiert. Wählen Sie Ihr Lieferziel.", skip: "Alles durchstöbern", pin: "Noch heute" },
};

export { CITY_STORAGE_KEY, rememberCity, readCityChoice } from "../lib/city";

const CityPickerModal = ({ onClose }) => {
  const { locale = "en" } = useParams();
  const { boot, setCityChoice } = useStore();
  const navigate = useNavigate();
  const dlgRef = useRef(null);
  const pat = PATTERNS[locale] || PATTERNS.en;
  const cities = useMemo(() => boot?.cities || [], [boot?.cities]);
  const copy = COPY[locale] || COPY.en;

  const pinned = useMemo(() =>
    cities
      .map((c) => ({ ...c, coords: CITY_COORDS[c.key] }))
      .filter((c) => c.coords), [cities]);

  // Lock scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Move focus in for a11y
    dlgRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const pick = (c) => {
    setCityChoice(c);
    onClose();
    navigate(`/${locale}/${pat.city}/${c.slug}`);
  };

  const skip = () => {
    // record a "no city" so we don't nag them again for 30 days
    skipCity();
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="city-picker-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/70 backdrop-blur-sm px-4 py-8 animate-in fade-in duration-300"
      onClick={(e) => { if (e.target === e.currentTarget) skip(); }}
      data-testid="city-picker-modal"
    >
      <div
        ref={dlgRef}
        tabIndex={-1}
        className="relative bg-plaster text-ink w-full max-w-3xl border border-brass shadow-2xl outline-none animate-in slide-in-from-bottom-4 duration-500"
      >
        <button
          type="button"
          onClick={skip}
          aria-label={copy.skip}
          data-testid="city-picker-close"
          className="absolute top-3 end-3 p-1 text-muted hover:text-ink transition-colors"
        >
          <X size={18} />
        </button>

        <div className="p-6 md:p-8 md:pb-4 space-y-2">
          <p className="smallcaps text-brass">Fleurinelle · {t(locale, "cities")}</p>
          <h2 id="city-picker-title" className="font-display text-2xl md:text-3xl leading-tight">{copy.title}</h2>
          <p className="text-sm text-charcoal max-w-xl">{copy.body}</p>
        </div>

        <div className="px-6 md:px-8 pb-6">
          <div className="relative mx-auto max-w-md aspect-square hidden sm:block">
            <img
              src="/brand/morocco-map.png"
              alt={t(locale, "mapAlt")}
              loading="eager"
              decoding="async"
              className="absolute inset-0 w-full h-full object-contain drop-shadow-md"
              style={{ animation: "mapFadeIn 700ms ease-out both" }}
            />
            {/* Pins as buttons layered on top of the map image */}
            {pinned.map((c, i) => (
              <button
                key={c.id}
                data-testid={`city-picker-pin-${c.key}`}
                type="button"
                onClick={() => pick(c)}
                className="absolute -translate-x-1/2 -translate-y-full group focus:outline-none"
                style={{ left: `${c.coords.x}%`, top: `${c.coords.y}%`, animation: `pinDrop 600ms ${420 + i * 90}ms both cubic-bezier(0.22, 1, 0.36, 1)` }}
                aria-label={c.name}
              >
                <span className="relative flex items-center justify-center">
                  <span className="absolute inline-block w-6 h-6 rounded-full bg-brass/45 animate-ping" />
                  <span className="relative z-10 w-3 h-3 rounded-full bg-ink border-2 border-plaster shadow-lg group-hover:scale-125 transition-transform" />
                </span>
                <span className="mt-1 block whitespace-nowrap text-[11px] font-medium tracking-wide bg-plaster border border-brass px-2 py-0.5 shadow-sm text-ink group-hover:bg-ink group-hover:text-plaster transition-colors">
                  {c.name}
                </span>
              </button>
            ))}
          </div>

          <ul className="mt-4 sm:mt-4 flex flex-wrap justify-center gap-2" data-testid="city-picker-chips">
            {pinned.map((c) => (
              <li key={c.id}>
                <button
                  data-testid={`city-picker-chip-${c.key}`}
                  onClick={() => pick(c)}
                  className="px-3 py-1.5 text-sm border border-brass-light text-charcoal hover:border-ink hover:bg-ink hover:text-plaster transition-colors"
                >
                  {c.name} <span className="text-[10px] opacity-70">· {copy.pin}</span>
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-6 text-center">
            <button
              onClick={skip}
              data-testid="city-picker-skip"
              className="text-xs text-muted hover:text-ink underline underline-offset-4"
            >
              {copy.skip}
            </button>
          </div>
        </div>
      </div>

      {/* pin drop animation */}
      <style>{`
        @keyframes pinDrop {
          from { transform: translate(-50%, -140%); opacity: 0; }
          to { transform: translate(-50%, -100%); opacity: 1; }
        }
        @keyframes mapFadeIn {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default CityPickerModal;
